import React, { FC, MouseEvent, ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { IsVarEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

type AdminListCountLinkProps = {
    readonly count: string | number | undefined | null
    readonly ariaLabel: string
} & ({ readonly to: string; readonly href?: never } | { readonly href: string; readonly to?: never }) // NOSONAR

const linkClassName: string = 'admin-list-count-link router-datatable-body-clickable'

/**
 * Renders a numeric sub-item count as an in-table link when the value is a positive integer;
 * otherwise renders plain text (including zero or empty).
 *
 * @param props - Count, navigation target ({@link Link} {@code to} or legacy {@code href}), and
 *   accessible label for the link.
 * @returns Table cell content.
 */
export const AdminListCountLink: FC<AdminListCountLinkProps> = (props: AdminListCountLinkProps): ReactElement => {
    const { count, ariaLabel }: AdminListCountLinkProps = props
    const raw: string = IsVarEmpty(count) ? EMPTY_STRING : String(count)
    const parsed: number = Number.parseInt(raw, 10)

    if (!isStringNotEmpty(raw) || Number.isNaN(parsed) || parsed <= 0) {
        return <>{raw === EMPTY_STRING ? EMPTY_STRING : raw}</>
    }

    const stopRowClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        event.stopPropagation()
    }

    if ('href' in props) {
        return (
            <a className={linkClassName} href={props.href} aria-label={ariaLabel} onClick={stopRowClick}>
                {raw}
            </a>
        )
    }

    return (
        <Link className={linkClassName} to={props.to} aria-label={ariaLabel} onClick={stopRowClick}>
            {raw}
        </Link>
    )
}
